import {
  Compass, Code2, Cloud, BrainCircuit, GitBranch, ShieldCheck, Headset,
  Rocket, LineChart, Layers, Boxes, Cpu, Database, Workflow,
  Landmark, HeartPulse, ShoppingBag, Factory, GraduationCap, Sparkles, Truck, Server,
  Target, Handshake, Gauge, BadgeIndianRupee, Megaphone,
} from "lucide-react";

export const BRAND = {
  name: "ATS IT Solutions",
  short: "ATS",
  expanded: "Atmasamman Technology Services",
  legal: "Atmasamman Pvt. Ltd.",
  domain: "atmasammantech.com",
  url: "https://atmasammantech.com",
  handle: "@AtmasammanTech",
};

export const CONTACT = {
  phone: "7505532934",
  phoneDisplay: "+91 75055 32934",
  email: "Director@atmasammantech.com",
  businessEmail: "support@atmasammantech.com",
  address: "98 Shivpuri, Near DAV Degree College, Reliance Smart Point, Bulandshahr 203001, UP, India",
  hours: "Mon – Sat, 10:00 AM – 7:00 PM IST",
};

export const SOCIALS = [
  { id: "youtube", name: "YouTube", label: "ATS IT Solutions", href: "https://www.youtube.com/@AtmasammanTech" },
  { id: "instagram", name: "Instagram", label: "ATS IT Solutions", href: "https://www.instagram.com/atmasammantech/" },
  { id: "facebook", name: "Facebook", label: "ATS IT Solutions", href: "https://www.facebook.com/profile.php?id=61591354752763" },
  { id: "x", name: "X", label: "ATS IT Solutions", href: "https://x.com/AtmasammanTech" },
  { id: "linkedin", name: "LinkedIn", label: "ATS IT Solutions", href: "https://www.linkedin.com/company/atmasammantech/" },
];

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Work", to: "/#work" },
  { label: "About", to: "/about" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const STATS = [
  { value: 16, suffix: "+", label: "Years MNC Tech Experience" },
  { value: 120, suffix: "+", label: "Projects Delivered" },
  { value: 30, suffix: "+", label: "Technologies Mastered" },
  { value: 99.9, suffix: "%", decimals: 1, label: "Uptime SLA Commitment" },
];

export const SERVICE_CATEGORIES = [
  {
    id: "consulting",
    icon: Compass,
    title: "IT Consulting & Strategy",
    desc: "Align technology with business outcomes through pragmatic, executive-grade advisory.",
    items: [
      "IT Strategy & Roadmap Planning",
      "Digital Transformation Consulting",
      "Technology Assessment & Advisory",
      "Solution & Enterprise Architecture",
      "Legacy System & IT Modernization",
      "Cloud Strategy",
    ],
  },
  {
    id: "software",
    icon: Code2,
    title: "Software & Application Development",
    desc: "Custom-engineered products, web, mobile and enterprise applications built for scale and reliability.",
    items: [
      "Custom Software Development",
      "Web Application Development",
      "Mobile App Development (iOS/Android)",
      "Enterprise & SaaS Application Development",
      "API Development & Integration",
      "Microservices & Application Modernization",
      "Software Maintenance & Enhancement",
      "UI/UX & Product Design",
    ],
  },
  {
    id: "cloud",
    icon: Cloud,
    title: "Cloud Solutions",
    desc: "Migrate, modernize and optimize across AWS, Azure and Google Cloud.",
    items: [
      "Cloud Consulting & Architecture",
      "Cloud Migration (AWS, Azure, GCP)",
      "Cloud-Native Development & Deployment",
      "Cloud Infrastructure Management",
      "Multi-Cloud & Hybrid Cloud",
      "Backup & Disaster Recovery",
      "Cloud Monitoring & Security",
      "Cloud Cost Optimization (FinOps)",
    ],
  },
  {
    id: "ai",
    icon: BrainCircuit,
    title: "AI, Data & Automation",
    desc: "Generative AI, custom agents, automation and data platforms that cut manual work and create measurable value.",
    items: [
      "Generative AI Solutions & AI Agents",
      "AI Chatbots & Virtual Assistants",
      "Business Process & Workflow Automation (IPA/RPA)",
      "Machine Learning Solutions",
      "Data Engineering & Integration",
      "Data Analytics, BI & Dashboards",
      "AI Integration with Existing Applications",
      "CRM & Lead Management Automation",
      "MLOps & AI Model Deployment",
    ],
  },
  {
    id: "devops",
    icon: GitBranch,
    title: "DevOps & Infrastructure",
    desc: "Ship faster and run reliably with automated, observable delivery pipelines.",
    items: [
      "DevOps Consulting & Implementation",
      "CI/CD Pipeline Setup",
      "Infrastructure as Code (IaC)",
      "Containerization & Orchestration (Docker, Kubernetes)",
      "Deployment & Infrastructure Automation",
      "Monitoring & Observability",
      "DevSecOps",
      "Site Reliability Engineering (SRE)",
    ],
  },
  {
    id: "security",
    icon: ShieldCheck,
    title: "Cybersecurity",
    desc: "Protect business-critical systems with assessments, compliance support and managed defense.",
    items: [
      "Security Assessment & Consulting",
      "Application Security Testing",
      "Cloud Security & Compliance Support",
      "Vulnerability Assessment",
      "Identity & Access Management (IAM)",
      "Data Protection",
      "Security Monitoring & Managed Security Services",
    ],
  },
  {
    id: "managed",
    icon: Headset,
    title: "Managed IT & Support",
    desc: "Keep operations running with 24/7 support, monitoring and expert technology resourcing.",
    items: [
      "Managed IT Services",
      "24/7 IT Support & Helpdesk",
      "Application & Infrastructure Support",
      "Cloud Operations & DevOps Support",
      "System & Performance Monitoring",
      "Backup Management",
      "Maintenance & Optimization",
      "IT Staffing / Staff Augmentation",
    ],
  },
  {
    id: "growth",
    icon: Megaphone,
    title: "Digital Marketing & Growth",
    desc: "Measurable growth that connects technology, marketing, automation and analytics.",
    items: [
      "Search Engine Optimization (SEO) & Local SEO",
      "Google Business Profile Optimization",
      "Social Media Marketing & Management",
      "Google Ads, Meta Ads & Performance Marketing",
      "Content & Email Marketing",
      "Lead Generation & Marketing Automation",
      "Conversion Rate & Landing Page Optimization",
      "Analytics & Reporting",
      "Social Media & Campaign Creatives, Brand Identity Support",
    ],
  },
];

export const SERVICE_DETAILS = {
  consulting: {
    tagline: "Technology strategy that maps directly to business outcomes.",
    overview: "ATS works with leadership teams to align technology investment with where the business is going. We assess the current landscape, define a pragmatic roadmap, help select the right platforms and architect for scale — so every engineering decision that follows has a clear reason behind it.",
    challenges: ["Unclear technology priorities and spend", "Ageing systems slowing the business down", "Platform decisions made without a long-term view"],
    outcomes: ["Executive-ready IT strategy & roadmap", "Target solution & enterprise architecture blueprint", "Modernization plan with phased risk control", "Vendor-neutral platform recommendations"],
    useCases: ["Digital transformation roadmap for a growing business", "Technology assessment before a major platform investment", "Modernizing a legacy system estate step by step"],
    tools: ["AWS", "Azure", "Google Cloud", "Kubernetes", "Terraform"],
    idealFor: ["BFSI", "Manufacturing", "Healthcare", "Education"],
  },
  software: {
    tagline: "Custom products, web, mobile and enterprise apps built to scale.",
    overview: "From greenfield products and SaaS platforms to enterprise systems and modernization of existing applications, ATS engineers software with clean architecture, rigorous testing and transparent agile delivery. UI/UX and product design are part of the build, and every release ships with documentation and a clear path to production support.",
    challenges: ["Manual processes that software should have replaced", "Legacy applications that are costly to change", "Disconnected systems that don't share data"],
    outcomes: ["Production-ready web, mobile, SaaS & enterprise apps", "Documented, versioned APIs & microservices", "UI/UX designed around real user journeys", "Sprint-based delivery with visible progress"],
    useCases: ["Customer-facing web & mobile app for a service business", "Internal enterprise application replacing spreadsheets", "Re-platforming a monolith into modern services"],
    tools: ["React", "Next.js", "Angular", "Vue", "Node.js", "Python", "Java", ".NET", "Django", "FastAPI", "PostgreSQL", "MongoDB"],
    idealFor: ["Startups", "SaaS", "Retail & E-commerce", "Logistics"],
  },
  cloud: {
    tagline: "Migrate, modernize and optimize across AWS, Azure and GCP.",
    overview: "ATS plans and executes cloud journeys end to end — from readiness assessment and migration to cloud-native deployment, backup & disaster recovery, monitoring and ongoing FinOps. Multi-cloud and hybrid designs keep you flexible and free of single-vendor lock-in.",
    challenges: ["Unpredictable, rising infrastructure costs", "On-premise systems limiting scale and resilience", "No tested recovery plan for outages"],
    outcomes: ["Cloud readiness assessment & migration plan", "Secure, well-architected landing zones", "Cloud-native services built for elasticity", "Backup, DR and continuous cost governance"],
    useCases: ["Lift-and-modernize migration of business applications", "Multi-cloud setup to reduce vendor lock-in", "Cost optimization of an existing cloud estate"],
    tools: ["AWS", "Azure", "Google Cloud", "Docker", "Kubernetes", "Terraform"],
    idealFor: ["Healthcare", "SaaS", "BFSI", "Retail & E-commerce"],
  },
  ai: {
    tagline: "Practical AI and automation that removes manual work and speeds up decisions.",
    overview: "ATS helps businesses adopt AI practically — not as a buzzword. We build custom AI agents, chatbots and virtual assistants, automate business processes and workflows, integrate generative AI into the applications you already run, and engineer the data pipelines, dashboards and MLOps foundations that keep everything accurate over time.",
    challenges: ["Teams spending hours on repetitive manual work", "Slow, inconsistent customer responses", "Data spread across tools with no single view"],
    outcomes: ["AI agents & chatbots wired into your workflows", "Automated processes with fewer manual hand-offs", "Dashboards and BI for data-driven decisions", "Reliable model deployment & monitoring (MLOps)"],
    useCases: ["AI assistant answering customer queries around the clock", "Automated lead qualification & CRM follow-ups", "Operational dashboards unifying data from multiple systems"],
    tools: ["Python", "TensorFlow", "PyTorch", "OpenAI", "FastAPI", "PostgreSQL", "MongoDB", "Redis"],
    idealFor: ["Retail & E-commerce", "BFSI", "Healthcare", "Logistics"],
    related: { id: "growth", text: "Applying automation to marketing and lead generation? See Digital Marketing & Growth." },
  },
  devops: {
    tagline: "Ship faster and run reliably with automated delivery pipelines.",
    overview: "ATS builds the delivery backbone your teams depend on — CI/CD, infrastructure as code, containers, deployment automation and observability — so releases are frequent, predictable and reversible, environments stay consistent, and production stays healthy.",
    challenges: ["Slow, manual and risky releases", "Inconsistent environments causing 'works on my machine' issues", "Limited visibility into production health"],
    outcomes: ["Automated CI/CD pipelines from commit to production", "Infrastructure as Code for repeatable environments", "Containerized, orchestrated workloads", "Monitoring, observability & SRE practices"],
    useCases: ["Setting up CI/CD for a product team from scratch", "Containerizing and orchestrating existing services", "Adding observability to a production platform"],
    tools: ["Docker", "Kubernetes", "Terraform", "Jenkins", "GitHub Actions", "AWS", "Azure"],
    idealFor: ["SaaS", "Startups", "BFSI", "Manufacturing"],
  },
  security: {
    tagline: "Protect business-critical systems with assessments, compliance support and managed defense.",
    overview: "ATS embeds security into architecture, code and operations. We assess what you run today, harden cloud and application layers, implement identity and access controls, protect data and provide ongoing security monitoring so risk stays visible and controlled.",
    challenges: ["Unknown vulnerabilities in applications and cloud", "Weak access controls across systems", "Compliance requirements without in-house expertise"],
    outcomes: ["Security assessment with prioritized remediation plan", "Application & cloud security hardening", "Identity & access management rollout", "Ongoing security monitoring"],
    useCases: ["Security review before launching a customer-facing platform", "IAM rollout across cloud and internal applications", "Compliance support for a regulated business"],
    tools: ["AWS", "Azure", "Google Cloud", "Kubernetes", "GitHub Actions"],
    idealFor: ["BFSI", "Healthcare", "SaaS", "Education"],
  },
  managed: {
    tagline: "Keep operations running with 24/7 support and expert resourcing.",
    overview: "ATS keeps your technology estate healthy day and night — helpdesk, application and infrastructure support, cloud operations, monitoring, backup management and skilled engineers on demand through staff augmentation — with clear SLAs and transparent reporting.",
    challenges: ["No reliable owner for day-to-day IT issues", "Downtime discovered by users before IT", "Difficulty hiring specialist engineers quickly"],
    outcomes: ["24/7 helpdesk & incident response", "Proactive monitoring & backup management", "SLA-backed managed IT operations", "Flexible staff augmentation for your teams"],
    useCases: ["Managed support for business applications after go-live", "Ongoing cloud operations for a lean internal team", "Augmenting a project team with ATS engineers"],
    tools: ["AWS", "Azure", "Kubernetes", "Jenkins", "PostgreSQL", "MongoDB"],
    idealFor: ["Manufacturing", "Education", "Retail & E-commerce", "Logistics"],
  },
  growth: {
    tagline: "Acquisition, engagement and conversion — measured, automated and tied to revenue.",
    overview: "Digital Marketing & Growth complements the technology ATS builds for you. We combine SEO and local visibility, social media, paid campaigns, content and email with marketing automation and analytics — so growth is measurable, repeatable and connected to the systems your business already runs on.",
    challenges: ["Low visibility in search and local results", "Ad spend without clear return", "Leads that go cold because follow-up is manual"],
    outcomes: ["Higher search & Google Business Profile visibility", "Performance campaigns with transparent reporting", "Automated lead capture, nurturing & follow-ups", "Landing pages and creatives built to convert"],
    useCases: ["Local SEO and Google Business Profile for a service business", "Performance marketing funnel with automated follow-ups", "Brand and campaign creatives for a product launch"],
    tools: ["Google Analytics", "Google Ads", "Meta Ads", "Google Search Console"],
    idealFor: ["Startups", "Retail & E-commerce", "Education", "Healthcare"],
    related: { id: "ai", text: "Marketing automation, AI chatbots for lead generation and CRM automation are engineered by our AI, Data & Automation team." },
  },
};

export const SERVICE_OPTIONS = [
  "IT Consulting",
  "Software Development",
  "Cloud Solutions",
  "AI, Data & Automation",
  "DevOps & Infrastructure",
  "Cybersecurity",
  "Managed IT & Support",
  "Digital Marketing & Growth",
  "Other",
];

export const WHY_CHOOSE = [
  {
    icon: Layers,
    title: "Multi-Domain Delivery",
    desc: "Proven expertise spanning consulting, cloud, AI, development and security — one accountable partner, end to end.",
  },
  {
    icon: Handshake,
    title: "Transparent Agile Engagement",
    desc: "Clear sprints, honest reporting and continuous feedback loops. No black boxes, no surprises.",
  },
  {
    icon: Rocket,
    title: "Latest Technology Stack",
    desc: "We adopt modern, battle-tested tooling early — so your systems stay fast, secure and future-ready.",
  },
  {
    icon: BadgeIndianRupee,
    title: "Cost-Effective, No Compromise",
    desc: "Enterprise-grade quality delivered efficiently — maximum value without cutting engineering corners.",
  },
];

export const TECH_STACK = [
  { name: "AWS", slug: "amazonwebservices", src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
  { name: "Azure", slug: "microsoftazure", src: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" },
  { name: "Google Cloud", slug: "googlecloud" },
  { name: "Python", slug: "python" },
  { name: "TensorFlow", slug: "tensorflow" },
  { name: "PyTorch", slug: "pytorch" },
  { name: "OpenAI", slug: "openai", src: "https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/openai.svg" },
  { name: "React", slug: "react" },
  { name: "Next.js", slug: "nextdotjs" },
  { name: "Angular", slug: "angular" },
  { name: "Vue", slug: "vuedotjs" },
  { name: "Node.js", slug: "nodedotjs" },
  { name: "Java", slug: "openjdk" },
  { name: ".NET", slug: "dotnet" },
  { name: "Django", slug: "django" },
  { name: "FastAPI", slug: "fastapi" },
  { name: "Docker", slug: "docker" },
  { name: "Kubernetes", slug: "kubernetes" },
  { name: "Terraform", slug: "terraform" },
  { name: "Jenkins", slug: "jenkins" },
  { name: "PostgreSQL", slug: "postgresql" },
  { name: "MongoDB", slug: "mongodb" },
  { name: "Redis", slug: "redis" },
  { name: "GitHub Actions", slug: "githubactions" },
  { name: "Google Analytics", slug: "googleanalytics" },
  { name: "Google Ads", slug: "googleads" },
  { name: "Meta Ads", slug: "meta" },
  { name: "Google Search Console", slug: "googlesearchconsole" },
];

export const PROCESS = [
  { n: "01", icon: Compass, title: "Discover", desc: "We understand your business goals, users and technical landscape before writing a line of code." },
  { n: "02", icon: LineChart, title: "Design", desc: "Solution architecture and blueprints — scalable, secure and mapped to real outcomes." },
  { n: "03", icon: Code2, title: "Develop", desc: "Agile sprints with continuous feedback, transparent progress and rigorous code quality." },
  { n: "04", icon: Rocket, title: "Deploy", desc: "Rigorous testing, automated pipelines and a smooth, low-risk go-live." },
  { n: "05", icon: Gauge, title: "Support", desc: "Ongoing monitoring, optimization and scaling as your business grows." },
];

export const INDUSTRIES = [
  { icon: Landmark, name: "BFSI" },
  { icon: HeartPulse, name: "Healthcare" },
  { icon: ShoppingBag, name: "Retail & E-commerce" },
  { icon: Factory, name: "Manufacturing" },
  { icon: GraduationCap, name: "Education" },
  { icon: Sparkles, name: "Startups" },
  { icon: Truck, name: "Logistics" },
  { icon: Server, name: "SaaS" },
];

export const CASE_STUDIES = [
  {
    industry: "FinTech / Payments",
    title: "Real-time payments modernization",
    image: "https://images.unsplash.com/photo-1771922748624-b205cf5d002d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    problem: "Legacy batch settlement caused slow reconciliation and rising ops cost.",
    solution: "Re-architected to an event-driven, cloud-native pipeline with automated reconciliation.",
    result: "40% faster processing",
  },
  {
    industry: "Retail / E-commerce",
    title: "AI-driven demand forecasting",
    image: "https://images.unsplash.com/photo-1745847768367-893e989d3a98?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    problem: "Overstock and stockouts from manual, guess-based inventory planning.",
    solution: "Deployed an ML forecasting engine integrated with the existing ERP.",
    result: "27% lower inventory cost",
  },
  {
    industry: "Healthcare SaaS",
    title: "Multi-cloud migration & FinOps",
    image: "https://images.unsplash.com/photo-1625296276397-3bc8839c688f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    problem: "Ballooning cloud spend and single-vendor lock-in risk.",
    solution: "Migrated to a hybrid multi-cloud setup with IaC and cost governance.",
    result: "60% cost reduction",
  },
  {
    industry: "Logistics",
    title: "Mobile fleet operations app",
    image: "https://images.unsplash.com/photo-1609921141835-710b7fa6e438?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    problem: "Paper-based dispatch created delays and zero live visibility.",
    solution: "Built a cross-platform mobile app with live tracking and automation.",
    result: "3x faster dispatch",
  },
];

export const AI_CAPABILITIES = [
  { icon: BrainCircuit, title: "Custom AI Agents", desc: "Autonomous, task-specific agents wired into your workflows and data." },
  { icon: Sparkles, title: "Generative AI Integration", desc: "LLM-powered assistants, copilots and content systems, safely deployed." },
  { icon: Workflow, title: "Intelligent Automation", desc: "IPA & RPA that removes repetitive work and cuts operating cost." },
  { icon: LineChart, title: "AI-Driven Analytics", desc: "Predictive insight and decision intelligence from your own data." },
];

export const VALUES = [
  { icon: ShieldCheck, title: "Quality", desc: "Engineering standards that hold up under real, business-critical load." },
  { icon: Cpu, title: "Innovation", desc: "Emerging tech applied with judgement — because it should solve a real problem." },
  { icon: Handshake, title: "Transparency", desc: "Honest communication, clear scope and visible progress at every step." },
  { icon: Target, title: "Long-Term Value", desc: "Solutions built to last, scale and keep paying back for years." },
];

export const TESTIMONIALS = [
  { quote: "ATS brought genuine enterprise discipline to our cloud migration. The delivery was transparent and on time.", name: "CTO", role: "Placeholder — FinTech Client" },
  { quote: "Their AI automation removed weeks of manual work every quarter. A partner that actually understands the business.", name: "Head of Ops", role: "Placeholder — Retail Client" },
  { quote: "Deep technical breadth across consulting, dev and security. We got one accountable team instead of five vendors.", name: "VP Engineering", role: "Placeholder — SaaS Client" },
];

export const FAQS = [
  { q: "How soon will I get a response?", a: "We typically respond within one business day. For urgent needs, call us directly and we'll prioritize your enquiry." },
  { q: "Do you take on both short-term and long-term projects?", a: "Yes. We support everything from focused short engagements and audits to long-term product development and managed services." },
  { q: "Can we schedule a call?", a: "Absolutely. Share your availability in the form or email us, and we'll set up a free consultation call at a convenient time." },
  { q: "Which technologies and clouds do you work with?", a: "We work across AWS, Azure and Google Cloud, and modern stacks including Python, React, Node, Java, .NET, Docker, Kubernetes and more." },
];

export const FOUNDER_BIO = [
  "Pankaj Sharma is a seasoned technology professional and entrepreneur with 16+ years of experience in the IT industry, working across enterprise technology, application and production support, digital payments, cloud technologies, databases, and emerging technologies including Artificial Intelligence.",
  "Throughout his professional journey, he has worked with and supported leading global organizations and brands including IBM, Tech Mahindra, BMC Software, Mastercard, EY, Unilever, and Macquarie Group, gaining extensive exposure to enterprise-scale systems, business-critical applications, and complex technology environments.",
  "As the Founder & CEO of ATS IT Solutions (ATS), Pankaj leads the company's vision of helping businesses transform ideas into reliable, scalable, and technology-driven solutions. ATS focuses on delivering services across software and web development, IT consulting, application support, cloud solutions, AI-enabled solutions, digital transformation, and technology services.",
  "His approach combines deep industry experience with a practical understanding of business challenges. He believes technology should not simply solve technical problems—it should create measurable value for businesses and their customers.",
  "His vision for ATS is to build a trusted technology organization that delivers quality, innovation, transparency, and long-term value while creating opportunities for the next generation of technology professionals.",
];

export const FOUNDER_TAGS = [
  "16+ Years IT Experience", "Enterprise Technology", "AI", "Cloud",
  "Digital Payments", "IT Consulting", "Technology Leadership",
];

export const CAREER_TYPES = [
  { id: "student", label: "Student / Internship Applicant", resume: "optional", resumeLabel: "Upload Resume (Optional)" },
  { id: "fresher", label: "Fresher / Recent Graduate", resume: "recommended", resumeLabel: "Upload Resume (Recommended)" },
  { id: "experienced", label: "Experienced Professional", resume: "required", resumeLabel: "Upload Resume *" },
];

export const CAREER_AREAS = [
  "Software Development", "Web Development", "Application Development", "Cloud", "AWS", "AI & Automation",
  "Data & Analytics", "DevOps", "Cybersecurity", "IT Support", "Digital Marketing", "UI/UX", "Other",
];
